import { Button, Box, ButtonProps, Drawer, Group, Badge, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFilter } from "@tabler/icons-react";
import _ from "lodash";

import { useSearchRouteInfo } from "hooks/routing";
import { QueryArgs } from "lib/frontend/benchmarking/types";
import { QUERY_ARGS_THAT_ARE_NOT_FILTERS } from "utils/utils";

import { FilterSidebar } from "./FilterSidebar";

export function FilterDrawerButton(buttonProps: Omit<ButtonProps, "onClick">) {
  const [opened, handlers] = useDisclosure(false);
  const { query } = useSearchRouteInfo();

  const numFilters = determineNumFilters(query);
  const theme = useMantineTheme();

  return (
    <>
      <Button
        variant="light"
        color="gray"
        radius={0}
        leftSection={<IconFilter size={16} />}
        onClick={handlers.open}
        bg="gray.1"
        sx={{
          borderTop: `1px solid var(--mantine-color-gray-3)`,
        }}
        {...buttonProps}
      >
        Filters{" "}
        {numFilters > 0 && (
          <Badge ml={8} size="xs" variant="filled" radius={50}>
            {numFilters}
          </Badge>
        )}
      </Button>
      <Drawer
        opened={opened}
        onClose={handlers.close}
        position="bottom"
        title="Filters"
        styles={{
          header: {
            paddingTop: 16,
            paddingLeft: 16,
            paddingRight: 8, // smaller to better align close button
            paddingBottom: 8, // smaller bc I guess Mantine adds some by default? idk.
          },

          /*
           * In Mantine v5, Drawers aren't scrollable out of the box.
           * This CSS hackery gets around this limitation and preserves
           * a fixed flex-container at the bottom.
           *
           * @see https://github.com/mantinedev/mantine/discussions/2483
           */
          content: {
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 72px)",

            // Unrelated to the scroll, but the radii look nice.
            borderTopLeftRadius: theme.radius.md,
            borderTopRightRadius: theme.radius.md,

            "& > :nth-of-type(2)": {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            },
          },
        }}
      >
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <FilterSidebar />
        </Box>
        <Group
          p={16}
          justify="center"
          sx={{
            flexShrink: 0,
            borderTop: `var(--mantine-color-gray-3) 1px solid`,
          }}
        >
          <Button onClick={handlers.close} w="100%" maw={320} variant="primary">
            Show Results
          </Button>
        </Group>
      </Drawer>
    </>
  );
}

// export type QueryArgs = {
//   title: string[];
//   companyName: string[];
//   stage: string[];
//   industry: string[];
//   location: Geographies[];
//   tier: string[];
//   headcount: string[];
//   capitalRaised: [number, number] | null
//   yoe: YOE_OPTIONS[];
//   requireRanges: boolean;
//   sortKey: ExplorerSortKey | null;
//   sortDir: ExplorerSortDir | null;
//   offset: string | number | null;
//   jobFamily: string[];
//   level: string[];
//   activeWithin: string | null;
// }

// shows number of types of filters that arent empty that are not title, jobFamily, level, offset, sortKey, sortDir
function determineNumFilters(query: QueryArgs): number {
  return _(query)
    .keys()
    .reduce((acc, key) => {
      if (QUERY_ARGS_THAT_ARE_NOT_FILTERS.includes(key)) {
        return acc;
        // @ts-expect-error TS7053
      } else if (_.isEmpty(query[key]) || _.isNil(query[key])) {
        return acc;
      } else {
        return acc + 1;
      }
    }, 0)
    .valueOf();
}
